import { type JSX, type ReactNode, useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router";

import type { UseCountryReturn } from "~/v2/api/hooks/useCountry";
import type { Account, Owner, OwnerDetails } from "~/v2/api/rest/account";
import type { Billing } from "~/v2/api/rest/billing";
import type { ElitePerks } from "~/v2/api/rest/elite";
import { Path } from "~/v2/path";
import { isPaperOrOnboarding } from "~/v2/utils";
import type { MarketDataPlans } from "../util";
import { AdditionalInfoConnector } from "./additionalInfo/AdditionalInfo.connector";
import { AgreementsConnector } from "./agreements/Agreements.connector";
import { EligibilityCheckConnector } from "./eligibilityCheck/EligibilityCheck.connector";
import { EligibilityCheckTitleTooltip } from "./eligibilityCheck/EligibilityCheckTitleTooltip";
import { ReviewConnector } from "./review/Review.connector";
import { SelectPlanConnector } from "./selectPlan/SelectPlan.connector";
import type { PaymentSchedule } from "./types";

const MARKET_DATA_STEP_NAMES = [
  "selectPlan",
  "eligibilityCheck",
  "additionalInfo",
  "agreements",
  "review",
] as const;

type StepName = (typeof MARKET_DATA_STEP_NAMES)[number];

// Props passed to component in SubscribeStep
export type SubscribeStepProps = {
  account: Account;
  details: OwnerDetails;
  elitePerks: ElitePerks | undefined;
  billing: Billing | undefined;
  plans: MarketDataPlans;
  isElite: boolean;
  isSubscribed: boolean;
  paymentSchedule: PaymentSchedule;
  isLoading: boolean;
  onCancel: () => void;
  onBackToPreviousStep: () => void;
  onContinueToNextStep: () => void;
  onPaymentScheduleChange: (paymentSchedule: PaymentSchedule) => void;
  country: UseCountryReturn;
  isLegalEntity: boolean;
  legalEntityOwner: Owner | undefined;
  accountOpener: Owner | undefined;
};

export type SubscribeStep = {
  title: string;
  description?: string;
  titleTooltip?: ReactNode;
  component: (props: SubscribeStepProps) => JSX.Element;
  step?: number;
  path: string;
  isPaperOnly?: boolean;
};

const STEPS: Record<StepName, SubscribeStep> = {
  selectPlan: {
    title: "Choose your plan",
    description: "Upgrade to unlock institutional-grade feeds.",
    component: SelectPlanConnector,
    path: Path.ROUTE_MARKET_DATA_SELECT_PLAN,
  },
  eligibilityCheck: {
    title: "Eligibility Check",
    description:
      "Verify your non-professional status (required by U.S. exchanges).",
    titleTooltip: <EligibilityCheckTitleTooltip />,
    component: EligibilityCheckConnector,
    path: Path.ROUTE_MARKET_DATA_ELIGIBILITY_CHECK,
  },
  additionalInfo: {
    title: "Additional Information",
    description: "Enter your information (required by U.S. exchanges).",
    component: AdditionalInfoConnector,
    path: Path.ROUTE_MARKET_DATA_ADDITIONAL_INFO,
    isPaperOnly: true,
  },
  agreements: {
    title: "Almost there!",
    description: "Review and accept the agreements to finish your application.",
    component: AgreementsConnector,
    path: Path.ROUTE_MARKET_DATA_AGREEMENTS,
  },
  review: {
    title: "Review & Subscribe",
    description: "Transparent pricing, cancel anytime.",
    component: ReviewConnector,
    path: Path.ROUTE_MARKET_DATA_REVIEW,
  },
};

type SubscribeRequiredSteps = Partial<typeof STEPS>;

export type SubscribeState = {
  stepName: StepName | null;
  paymentSchedule: PaymentSchedule;
  isLoading: boolean;
};

const DEFAULT_STATE: SubscribeState = {
  stepName: "selectPlan",
  paymentSchedule: "yearly",
  isLoading: false,
};

const getSessionStorageKey = (account?: Account) =>
  `market-data-sub-${account?.id}`;

interface SubscribeControllerProps {
  account: Account | undefined;
  details: OwnerDetails | undefined;
  isDataLoading: boolean;
}

export type SubscribeControllerReturn = {
  step: SubscribeStep | undefined;
  totalSteps: number | undefined;
  isLoading: boolean;
  onCancel: () => void;
  onBackToPreviousStep: () => void;
  onContinueToNextStep: () => void;
  onPaymentScheduleChange: (paymentSchedule: PaymentSchedule) => void;
  paymentSchedule: PaymentSchedule;
};

export const useMarketDataSubscribeController = ({
  account,
  details,
  isDataLoading,
}: SubscribeControllerProps): SubscribeControllerReturn => {
  const history = useHistory();

  const [state, setState] = useState<SubscribeState>({
    stepName: null,
    paymentSchedule: "yearly",
    isLoading: true,
  });

  const onSaveState = (partialState: Partial<SubscribeState>) => {
    const newState = { ...state, ...partialState };
    setState(newState);

    if (account) {
      try {
        window?.sessionStorage?.setItem(
          getSessionStorageKey(account),
          JSON.stringify(newState),
        );
      } catch (error) {
        console.error(
          "Failed to save market data subscription state to session storage",
          error,
        );
      }
    }

    syncPathnameToStepName(newState.stepName);
  };

  const onDeleteState = () => {
    try {
      window?.sessionStorage?.removeItem(getSessionStorageKey(account));
    } catch (error) {
      console.error(
        "Failed to remove market data subscription state from session storage",
        error,
      );
    }

    setState(DEFAULT_STATE);
  };

  const onCancel = () => {
    onDeleteState();
    history.push(Path.ROUTE_DASHBOARD);
  };

  const syncPathnameToStepName = (step: StepName | null) =>
    window?.history?.replaceState(null, "", STEPS[step ?? "selectPlan"].path);

  const filteredSteps = useMemo(() => {
    const isPaper = isPaperOrOnboarding(account);

    // Length used to add step number to steps after selectPlan
    const stepNumbers: StepName[] = [];

    return (
      Object.entries(STEPS)
        // Filter out paper only steps if account is live
        .filter(([_key, step]) => {
          if (!isPaper && step.isPaperOnly) {
            return false;
          }
          return true;
        })
        .reduce((acc, [key, step]) => {
          // Add step number to all steps after selectPlan
          if (key !== "selectPlan") {
            stepNumbers.push(key as StepName);
            step.step = stepNumbers.length;
          }

          acc[key as StepName] = step;
          return acc;
        }, {} as SubscribeRequiredSteps)
    );
  }, [account]);

  const navigateToStep = (direction: "previous" | "next") => {
    if (!state.stepName) {
      return;
    }

    const stepNames = Object.keys(filteredSteps) as StepName[];
    const currentIndex = stepNames.indexOf(state.stepName);
    const targetIndex =
      direction === "next" ? currentIndex + 1 : currentIndex - 1;
    const targetStepName = stepNames.at(targetIndex);

    if (targetStepName) {
      onSaveState({ stepName: targetStepName });
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onBackToPreviousStep = () => navigateToStep("previous");

  const onContinueToNextStep = () => navigateToStep("next");

  const onPaymentScheduleChange = (paymentSchedule: "monthly" | "yearly") =>
    onSaveState({ paymentSchedule });

  // biome-ignore lint/correctness/useExhaustiveDependencies: account?.id and details?.owner_id are deliberately narrower than the objects: this effect writes state, and the fetched objects get a new identity on every query settle, so depending on them would loop. filteredSteps and syncPathnameToStepName are rebuilt every render.
  useEffect(() => {
    if (!account?.id || !details?.owner_id) {
      return;
    }

    try {
      const sessionString = window?.sessionStorage?.getItem(
        getSessionStorageKey(account),
      );

      let session: SubscribeState = sessionString
        ? JSON.parse(sessionString)
        : { ...DEFAULT_STATE };

      if (
        !session ||
        !filteredSteps[session?.stepName as StepName] ||
        !(
          session?.paymentSchedule === "monthly" ||
          session?.paymentSchedule === "yearly"
        )
      ) {
        session = { ...DEFAULT_STATE };
      }

      syncPathnameToStepName(session.stepName);
      setState({ ...session, isLoading: false });
    } catch (error) {
      console.error("session state error", error);
      setState(DEFAULT_STATE);
    }
  }, [account?.id, details?.owner_id]);

  const step = state.stepName ? filteredSteps[state.stepName] : undefined;
  const totalSteps = step?.step
    ? Object.keys(filteredSteps).filter((key) => key !== "selectPlan").length
    : undefined;
  const paymentSchedule = state.paymentSchedule;
  const isLoading = !step || isDataLoading || state.isLoading;

  return {
    step,
    totalSteps,
    isLoading,
    onCancel,
    onBackToPreviousStep,
    onContinueToNextStep,
    onPaymentScheduleChange,
    paymentSchedule,
  };
};
